import { useEffect, useRef } from 'react';
import { useCanvasSession } from '../contexts/CanvasSessionContext';
import { useUiOverlay } from '../contexts/useUiOverlay';
import { openSiteOnCanvas } from '../lib/open-site-on-canvas';
import { openPanelInCanvas } from 'agentable-canvas/whiteboard';

const CHAT_PANEL_WIDTH = 400;
const BRIEF_PANEL_WIDTH = 380;

const ONBOARDING_PANEL_PHASES = new Set(['onboarding', 'generating']);

/**
 * Opens grouped site panels when onboarding starts (delegates to openSiteOnCanvas).
 * Replaces ad-hoc chat+brief placement so chat docks flush-left in the site group.
 */
export function useOnboardingPanelsBridge(): void {
  const { session } = useCanvasSession();
  const { homeVisible } = useUiOverlay();
  const openedForPhaseRef = useRef<string | null>(null);

  useEffect(() => {
    if (homeVisible || !ONBOARDING_PANEL_PHASES.has(session.phase)) {
      openedForPhaseRef.current = null;
      return;
    }

    const siteId = session.siteId;
    if (!siteId) return;

    const sessionKey = `${session.phase}:${session.updatedAt}`;
    if (openedForPhaseRef.current === sessionKey) {
      return;
    }

    const cleanup = openSiteOnCanvas({ siteId, session });
    openedForPhaseRef.current = sessionKey;
    return cleanup;
  }, [session.phase, session.updatedAt, session.siteId, session, homeVisible]);
}

/** Mount helper — call from App shell. */
export function OnboardingPanelsBridgeMount(): null {
  useOnboardingPanelsBridge();
  return null;
}

/** Imperatively focus/open the project brief panel from sidebar nav. */
export function openProjectBriefPanel(): void {
  openPanelInCanvas('project-brief', {
    focus: true,
    preserveZoom: true,
    size: { w: BRIEF_PANEL_WIDTH, h: 560 },
    panelProps: { __title: 'Project Brief' },
  });
}

/** Imperatively focus/open the chat panel from sidebar nav. */
export function openOnboardingChatPanel(): void {
  openPanelInCanvas('chat', {
    focus: true,
    preserveZoom: true,
    size: { w: CHAT_PANEL_WIDTH, h: 520 },
    panelProps: { __title: 'Chat' },
  });
  window.dispatchEvent(new CustomEvent('landi:focus-chat-input'));
}
